<template>
  <main-layout>
    <section class="l-content-area">
      <div class="l-content l-content--login">
        <div class="l-wrap__outer">
          <div class="l-wrap__inner">
            <section class="c-title__bloc c-title__bloc--login">
              <h1>{{ formText.title }}</h1>
            </section>
            <p class="contact-message">
              {{ formText.heading }}
            </p>

            <form @submit.prevent="submitForm" class="contact-form">
              <div class="mb-6">
                <label>{{ text.name }}</label>
                <div class="name-fields">
                  <input
                    v-model="form.last_name"
                    :class="{
                      'no-border': formValidated,
                    }"
                    :readonly="formValidated"
                    type="text"
                    placeholder="姓"
                  />
                  <input
                    v-model="form.first_name"
                    :class="{
                      'no-border': formValidated,
                    }"
                    :readonly="formValidated"
                    placeholder="名"
                    type="text"
                  />
                </div>
                <p v-if="errors.last_name || errors.first_name" class="error-message">
                  {{ errors.last_name || errors.first_name }}
                </p>
              </div>

              <div class="mb-6">
                <label>{{ text.kana }}</label>
                <div class="name-fields">
                  <input
                    v-model="form.last_name_kana"
                    :class="{
                      'no-border': formValidated,
                    }"
                    :readonly="formValidated"
                    type="text"
                    placeholder="セイ"
                  />
                  <input
                    v-model="form.first_name_kana"
                    :class="{
                      'no-border': formValidated,
                    }"
                    :readonly="formValidated"
                    type="text"
                    placeholder="メイ"
                  />
                </div>
                <p v-if="errors.last_name_kana || errors.first_name_kana" class="error-message">
                  {{ errors.last_name_kana || errors.first_name_kana }}
                </p>
              </div>

              <div class="mb-6">
                <label>{{ text.customer_no }}</label>
                <input
                  v-model="form.customer_no"
                  :class="{
                    'no-border': formValidated,
                  }"
                  :placeholder="placeholder.customer_no"
                  :readonly="formValidated"
                  type="text"
                  @input="numbersOnly('customer_no')"
                />
                <p v-if="errors.customer_no" class="error-message">
                  {{ errors.customer_no }}
                </p>
              </div>

              <div class="mb-6">
                <label>{{ text.email }}</label>
                <input
                  v-model="form.email"
                  :class="{
                    'no-border': formValidated,
                  }"
                  :placeholder="placeholder.email"
                  :readonly="formValidated"
                  type="email"
                  filled
                />
                <p v-if="errors.email" class="error-message">
                  {{ errors.email }}
                </p>
              </div>

              <div class="mb-6">
                <label>{{ text.phone_number }}</label>
                <input
                  v-model="form.phone_number"
                  :class="{
                    'no-border': formValidated,
                  }"
                  :placeholder="placeholder.phone_number"
                  :readonly="formValidated"
                  type="text"
                  @input="numbersOnly('phone_number')"
                  filled
                />
                <p v-if="errors.phone_number" class="error-message">
                  {{ errors.phone_number }}
                </p>
              </div>

              <div class="mb-6">
                <label>{{ text.store_id }}</label>
                <v-select
                  v-model="form.store_id"
                  :readonly="formValidated"
                  :outlined="!formValidated"
                  :placeholder="placeholder.store_id"
                  :items="options"
                  :loading="!options.length"
                  filled
                  hide-details
                />

                <p v-if="errors.store_id" class="error-message">
                  {{ errors.store_id }}
                </p>
              </div>

              <div class="mb-14">
                <label>{{ text.inquiry_detail }}</label>
                <v-textarea
                  v-model="form.inquiry_detail"
                  :readonly="formValidated"
                  :outlined="!formValidated"
                  :placeholder="placeholder.inquiry_detail"
                  row-height="100"
                  rows="2"
                  auto-grow
                  filled
                  hide-details
                />

                <p v-if="errors.inquiry_detail" class="error-message">
                  {{ errors.inquiry_detail }}
                </p>
              </div>

              <div v-if="!formValidated" class="captcha-wrapper">
                <vue-recaptcha
                  ref="recaptcha"
                  :sitekey="SITE_KEY"
                  @verify="onVerify"
                  @expired="onExpired"
                />
              </div>

              <div>
                <button-registration
                  :next-text="nextButton"
                  :prev-text="previousButton"
                  :handle-next-button="nextStep"
                  :handle-prev-button="previousStep"
                  :is-next-button="true"
                  :isDisable="!this.formValidated && !this.recaptchaToken"
                  class="pt-0"
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <v-overlay :opacity="0.25" :value="loading">
        <v-progress-circular :size="70" :width="7" color="#0a005a" indeterminate />
      </v-overlay>
    </section>
  </main-layout>
</template>

<script>
import * as yup from 'yup'
import * as errorString from '../../constant/text'
import * as regex from '../../helpers/regex'
import ButtonRegistration from '../common/ButtonRegistration'
import MainLayout from '../../components/layout/MainLayout'
import { getStudio } from '../../actions/requestReservation'
import { handleErrors } from '../../helpers/commonHelpers'
import { inquiry } from '../../actions/requestLogin'
import VueRecaptcha from 'vue-recaptcha'
import { isValid } from 'ipaddr.js'

const formSchema = yup.object().shape({
  last_name: yup.string().required(errorString.FIELD_REQUIRED),
  first_name: yup.string().required(errorString.FIELD_REQUIRED),
  last_name_kana: yup
    .string()
    .matches(regex.onlyKana, 'セイ（カナ）は、カタカナで入力してください')
    .required(errorString.FIELD_REQUIRED),
  first_name_kana: yup
    .string()
    .matches(regex.onlyKana, 'メイ（カナ）は、カタカナで入力してください')
    .required(errorString.FIELD_REQUIRED),
  customer_no: yup
    .string()
    .matches(regex.inquiryCustomerNumber, '8桁の数字を入力してください')
    .required(errorString.FIELD_REQUIRED),
  email: yup
    .string()
    .matches(regex.inquiryEmail, '有効なメールアドレスを入力してください')
    .max(300, '有効なメールアドレスを入力してください')
    .required(errorString.FIELD_REQUIRED),
  phone_number: yup
    .string()
    .matches(regex.inquiryPhoneNumber, '携帯電話番号は、ハイフンなしの11桁で入力してください')
    .required(errorString.FIELD_REQUIRED),
  store_id: yup.string().required('店舗を選択してください'),
  inquiry_detail: yup
    .string()
    .max(2000, '2000文字以内で入力してください')
    .required(errorString.FIELD_REQUIRED),
})

export default {
  name: 'Contact',
  components: { ButtonRegistration, MainLayout, VueRecaptcha },
  mounted() {
    this.getAllStudios()
  },
  data() {
    return {
      recaptchaToken: '',
      options: [],
      formValidated: false,
      formValidatedOnce: false,
      loading: false,
      errors: {},
      text: {
        title: 'アカウントに関するお問合せ',
        confirmTitle: 'お問合せ内容の確認',
        heading:
          '予約システムを利用する際に、アカウント連携できない場合はこちらよりお問合せをお願いいたします。またお問合せ内容項目に、アカウント連携ができなかった際の詳細状況を入力してください。',
        confirmHeading: '入力内容を確認して送信するボタンをクリックしてください。',
        name: 'お名前',
        kana: 'フリガナ',
        customer_no: '顧客番号',
        email: 'メールアドレス',
        phone_number: 'ご連絡先',
        store_id: 'ご利用店舗',
        inquiry_detail: 'お問合せ内容',
      },
      placeholder: {
        customer_no: '12345678',
        email: 'test@svenson.co.jp',
        phone_number: '09012345678',
        store_id: '選択してください',
        inquiry_detail: '内容を入力してください',
      },
      form: {
        last_name: '',
        first_name: '',
        last_name_kana: '',
        first_name_kana: '',
        customer_no: '',
        email: '',
        phone_number: '',
        store_id: '',
        inquiry_detail: '',
      },
    }
  },
  watch: {
    form: {
      handler(newForm) {
        Object.keys(newForm).forEach((field) => {
          this.validateField(field) // validates on change after the 1st submission attempt
        })
      },
      deep: true,
    },
  },
  computed: {
    SITE_KEY() {
      return process.env.VUE_APP_RECAPTCHA_SITE_KEY
    },
    formText() {
      const { title, heading, confirmTitle, confirmHeading } = this.text

      return {
        title: !this.formValidated ? title : confirmTitle,
        heading: !this.formValidated ? heading : confirmHeading,
      }
    },
    nextButton() {
      return !this.formValidated ? '入力内容を確認' : '送信する'
    },
    previousButton() {
      return !this.formValidated ? 'ログイン画面に戻る' : '戻る'
    },
  },
  methods: {
    async onVerify(token) {
      this.recaptchaToken = token
    },

    async submitForm() {
      const formData = { ...this.form, 'g-recaptcha-response': this.recaptchaToken }
      this.loading = true

      try {
        const response = await inquiry(formData)

        if (response.data?.success === 1) {
          this.$router.push({ name: 'contact-confirm' })
        }
      } catch (error) {
        this.$toast.error(handleErrors(error))
      } finally {
        // Reset the form and the reCAPTCHA token after submission
        this.loading = false
        this.recaptchaToken = null
        this.$refs.recaptcha.reset()
      }
    },

    onExpired() {
      this.recaptchaToken = null
      this.$refs.recaptcha.reset()
    },

    previousStep() {
      return this.formValidated
        ? ((this.formValidated = false), (this.recaptchaToken = null))
        : this.$router.push({ name: 'login' })
    },
    async nextStep() {
      if (this.formValidated) return this.submitForm()
      window.scrollTo(0, 0)
      try {
        this.errors = {}
        this.formValidatedOnce = true

        await formSchema.validate(this.form, { abortEarly: false })
        this.formValidated = true
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          this.errors = {}
          err.inner.forEach((error) => {
            this.$set(this.errors, error.path, error.message)
          })
        }
      }
    },
    async validateField(field) {
      // this is so there is no validation on change until the form is at least (tried to be) submitted once
      if (!this.formValidatedOnce) return

      try {
        await yup.reach(formSchema, field).validate(this.form[field])
        this.$delete(this.errors, field)
      } catch (error) {
        this.$set(this.errors, field, error.message)
      }
    },
    numbersOnly(field) {
      this.form[field] = this.form[field].replace(/\D/g, '')
    },
    getAllStudios() {
      if (this.options.length) return // prevents multiple fetches

      getStudio()
        .then((response) => {
          const {
            data: { success, data },
          } = response

          if (success === 1) {
            this.options = data.map((studio) => ({
              // mutates the stores to populate the dropdown correctly
              text: studio.name,
              value: studio.id,
            }))
          }
        })
        .catch((error) => {
          if (error) {
            return this.$toast.error(handleErrors(error))
          }
        })
    },
  },
}
</script>

<style scoped lang="scss">
.contact-message {
  font-size: 14px;
  padding: 10px 6px;
  margin: 0 auto;
  max-width: 440px;
  white-space: pre-wrap;
  color: rgba(0, 0, 0, 0.87);
}

.contact-form {
  max-width: 440px;
  margin: 0 auto;
  margin-top: 40px;
}

label {
  font-weight: bold;
  display: block;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.87);

  &::after {
    content: ' *';
    color: red;
  }
}

.no-border {
  border: none;
}

.name-fields {
  display: flex;
  gap: 20px;
}

.error-message {
  color: red;
  font-size: 14px;
  margin-top: 6px;
}

input,
select,
textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
}
::v-deep(.v-text-field > .v-input__control > .v-input__slot:after) {
  border: none !important;
}
::v-deep(.theme--light.v-text-field > .v-input__control > .v-input__slot:before) {
  border: none !important;
}
textarea {
  height: 100px;
  resize: none;
}

input[type='text'],
input[type='email'],
input[type='tel'] {
  background-color: #f5f5f5;
  border-color: #959595;
  height: 48px;
  padding: 8px 20px;
}

::v-deep(.v-input__slot) {
  padding: 0px 20px !important;
}

::v-deep(.v-textarea.v-text-field--enclosed textarea) {
  margin-top: 12px !important;
}

.captcha-wrapper {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}
</style>
